import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import { useNavigate, useRouteLoaderData } from '@remix-run/react';
import type {
  Price as DreamPrice,
  Tier as DreamTier,
  StripeCardElementComponent,
  TPricingElement,
} from '@shared/dream-components';
import { currencyFormatter, PricingElement } from '@shared/dream-components';
import { CardElement, Elements, PaymentRequestButtonElement } from '@stripe/react-stripe-js';
import { produce } from 'immer';

import SignupAction from '~/components/SignupAction';
import Text from '~/components/Text';
import UpgradeAction from '~/components/UpgradeAction';
import { UpgradeContext } from '~/context/UpgradeContext';
import { UpgradeProvider } from '~/context/UpgradeProvider';
import useSignupRedirects from '~/hooks/useUtils/useSignupRedirects';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { PricingDetails, Price as SwarmPrice, Tier as SwarmTier } from '~/interfaces/subscriptions';
import type { RootLoaderData } from '~/root';
import type { StaticPageLoaderData } from '~/services/static_page';

interface Props {
  node: TPricingElement;
  children: ReactNode | ReactNode[];
}

const mapTier = (
  tier: DreamTier,
  validTiers: Record<string, SwarmTier>,
  validPricesMap: Record<string, SwarmPrice>,
) => {
  if (tier.id in validTiers) {
    tier.prices = produce(tier.prices, (draftPrices: DreamPrice[]) => {
      draftPrices
        .filter((price) => validPricesMap[price.id])
        .map((price) => ({
          id: validPricesMap[price.id].id,
          amount_cents: validPricesMap[price.id].amount_cents,
          original_amount_cents: validPricesMap[price.id].original_amount_cents,
          currency: validPricesMap[price.id].currency,
          interval: validPricesMap[price.id].interval,
          cta: validPricesMap[price.id].cta,
          denominator: validPricesMap[price.id].denominator,
          features: validPricesMap[price.id].features,
          premium_offer: validPricesMap[price.id].premium_offer,
        }));
    });
  }
  return tier;
};

// Swap tiers out that might not be applicable to the current subscriber
// based on their subscription
const swapTiersInNode = (node: TPricingElement, pricingData: PricingDetails) => {
  const validPricesMap: Record<string, SwarmPrice> = {};
  const validGiftPricesMap: Record<string, SwarmPrice> = {};

  // Make a hash of valid tiers downloaded on the client side
  const validTiersById: Record<string, SwarmTier> = (pricingData?.tiers || []).reduce((previousValue, currentValue) => {
    const result: Record<string, SwarmTier> = Object.assign({}, previousValue);
    result[currentValue.id] = currentValue;

    // Map prices
    (currentValue.prices || []).forEach((price) => {
      validPricesMap[price.id] = price;
    });
    return result;
  }, {});

  // Make a hash of valid gift tiers downloaded on the client side
  const validGiftTiersById: Record<string, SwarmTier> = (pricingData?.gift_tiers || []).reduce(
    (previousValue, currentValue) => {
      const result: Record<string, SwarmTier> = Object.assign({}, previousValue);
      result[currentValue.id] = currentValue;

      // Map prices
      (currentValue.prices || []).forEach((price) => {
        validGiftPricesMap[price.id] = price;
      });
      return result;
    },
    {},
  );

  return produce(node, (draft) => {
    draft.attrs.data = draft.attrs.data
      .filter((tier) => {
        return tier.id === 'free' || validTiersById[tier.id];
      })
      .map((tier) => mapTier(tier, validTiersById, validPricesMap));

    // Update node and evaluate gift tiers by looking up any
    // gift tiers based on the tiers chosen in the website builder
    // by the user
    // Example;
    // 1. User created 3 tiers
    // 2. Tier 1 and 2 has gifting turned on
    // 3. User selects tier 1 and 3 in website builder
    // 4. We display "Tier 1" as a Tier to gift, but not tier 2
    // 5. We omit "Tier 2" because the user has not selected that
    //    tier in the website builder
    draft.attrs.giftTiersData = draft.attrs.data
      .filter((tier) => {
        return validGiftTiersById[tier.id];
      })
      .map((tier) => mapTier(tier, validGiftTiersById, validGiftPricesMap));
  });
};

const ErrorBoundaryFallback = ({ message }: { message: string }) => (
  <div className="flex flex-col max-w-2xl py-8 space-y-2">
    <Text as="p" type="body" size="md" className="leading-tight tracking-normal text-center">
      {message}
    </Text>
  </div>
);

const Pricing = ({ node, children }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { nextPage, nextStep } = useSignupRedirects();
  const { publication, site } = useRouteLoaderData('root') as RootLoaderData;
  const { pricingData, hasPricingDataFetchError, hasPricingDataFetched } = useTypedLoaderData<StaticPageLoaderData>();

  const [renderableNode, setRenderableNode] = useState(node);
  const activeTiers = useMemo(
    () => (renderableNode.attrs.data || []).filter((tier) => !!tier.enabled) || [],
    [renderableNode.attrs.data],
  );
  const activeGiftTiers = useMemo(
    () => (renderableNode.attrs.giftTiersData || []).filter((tier) => !!tier.enabled) || [],
    [renderableNode.attrs.giftTiersData],
  );

  const canShowPaymentRequestButton =
    renderableNode.attrs.internalCheckout === true ? publication?.stripe_payment_method_domain_enabled : false;
  const isTermsOfServiceEnabled = !!site.viewable_site_version.settings.terms_of_service_enabled;
  const hasCustomTermsOfService = site.viewable_site_version.settings.terms_of_service;
  const isCollectingBillingAddress = renderableNode.attrs.collectBillingAddress || false;

  useEffect(() => {
    if (hasPricingDataFetched && !hasPricingDataFetchError && pricingData && node.attrs.hasFakeData !== true) {
      setRenderableNode(swapTiersInNode(node, pricingData));
    }
  }, [hasPricingDataFetched, hasPricingDataFetchError, pricingData, node, node.attrs.hasFakeData]);

  return (
    <ErrorBoundary fallbackRender={(props) => <ErrorBoundaryFallback message={props.error.message} />}>
      <UpgradeProvider
        giftTiers={activeGiftTiers}
        tiers={activeTiers}
        isInternalCheckout={renderableNode.attrs.internalCheckout}
        recommendedTierId={renderableNode.attrs.recommendedTierId}
        recommendedPriceId={renderableNode.attrs.recommendedPriceId}
      >
        <UpgradeContext.Consumer>
          {(contextValue) => {
            if (hasPricingDataFetchError) {
              throw new Error('RefreshPageError');
            }

            // If the user has selected a free tier
            // or a price with premium offer that does
            // not require payment (while internal checkout is enabled)
            // then let them signup using signup action
            if (
              (contextValue.isInternalCheckout && contextValue.selectedTier?.id === 'free') ||
              (contextValue.isInternalCheckout &&
                contextValue.selectedPrice?.premium_offer &&
                !contextValue.selectedPrice?.premium_offer?.card_required)
            ) {
              return (
                <SignupAction
                  id={renderableNode.attrs.id}
                  premiumOfferId={contextValue.selectedPrice?.premium_offer?.id}
                  redirectUrl={contextValue.selectedTier?.redirect_url}
                >
                  {({ isLoading, setEmail }) => {
                    return (
                      <PricingElement
                        isCollectingBillingAddress={isCollectingBillingAddress}
                        element={renderableNode}
                        isInternalCheckout={renderableNode.attrs.internalCheckout}
                        selectedTier={contextValue.selectedTier}
                        onTierClick={contextValue.setSelectedTier}
                        selectedPrice={contextValue.selectedPrice}
                        onPriceClick={contextValue.setSelectedPrice}
                        isStripeLoading={false}
                        stripeSessionIds={{}}
                        email={contextValue.email}
                        onEmailChange={setEmail}
                        showTaxIdField={false}
                        taxId={contextValue.taxId}
                        onTaxIdChange={contextValue.setTaxId}
                        currentFormMode={contextValue.currentFormMode}
                        donationAmountCents={contextValue.donationAmountCents}
                        onDonationAmountCentsChange={contextValue.setDonationAmountCents}
                        isSubmitting={isLoading}
                        hasTermsOfService={isTermsOfServiceEnabled}
                        hasCustomTermsOfService={hasCustomTermsOfService}
                      >
                        {children}
                      </PricingElement>
                    );
                  }}
                </SignupAction>
              );
            }

            if (!contextValue.stripeSession) {
              return null;
            }

            const handleSuccess = () => {
              // Redirect to selected tier's redirect_url if it is defined
              const tierRedirectUrl =
                contextValue.currentFormMode === 'gift'
                  ? contextValue.selectedGiftTier?.redirect_url
                  : contextValue.selectedTier?.redirect_url;
              if (tierRedirectUrl && typeof window !== 'undefined') {
                window.location.href = tierRedirectUrl;
                return;
              }

              if (nextStep) {
                nextPage();
                return;
              }

              navigate('/');
            };

            return (
              <Elements stripe={contextValue.stripeSession}>
                <UpgradeAction
                  canShowPaymentRequestButton={canShowPaymentRequestButton}
                  onSuccess={handleSuccess}
                  isCollectingBillingAddress={renderableNode.attrs.collectBillingAddress}
                >
                  {({ isSubmitting, paymentRequest, setEmail }) =>
                    contextValue.stripeSession ? (
                      <PricingElement
                        key={contextValue.currentFormMode}
                        isCollectingBillingAddress={isCollectingBillingAddress}
                        element={renderableNode}
                        isInternalCheckout={renderableNode.attrs.internalCheckout}
                        selectedTier={
                          contextValue.currentFormMode === 'gift'
                            ? contextValue.selectedGiftTier
                            : contextValue.selectedTier
                        }
                        onTierClick={
                          contextValue.currentFormMode === 'gift'
                            ? contextValue.setSelectedGiftTier
                            : contextValue.setSelectedTier
                        }
                        selectedPrice={
                          contextValue.currentFormMode === 'gift'
                            ? contextValue.selectedGiftPrice
                            : contextValue.selectedPrice
                        }
                        onPriceClick={
                          contextValue.currentFormMode === 'gift'
                            ? contextValue.setSelectedGiftPrice
                            : contextValue.setSelectedPrice
                        }
                        isStripeLoading={contextValue.isStripeLoading}
                        email={contextValue.email}
                        onEmailChange={setEmail}
                        showTaxIdField={contextValue.showTaxIdField}
                        taxId={contextValue.taxId}
                        onTaxIdChange={contextValue.setTaxId}
                        donationAmountCents={contextValue.donationAmountCents}
                        onDonationAmountCentsChange={contextValue.setDonationAmountCents}
                        isSubmitting={isSubmitting}
                        translations={{
                          'upgrade.tabs.gift.title': t('upgrade.tabs.gift.title'),
                          'upgrade.tabs.individual.title': t('upgrade.tabs.individual.title'),
                          'upgrade.tabs.gift.form.title': t('upgrade.tabs.gift.form.title', {
                            price: contextValue?.selectedGiftPrice
                              ? currencyFormatter({ currency: contextValue.selectedGiftPrice.currency }).format(
                                  contextValue.selectedGiftPrice.amount_cents / 100,
                                )
                              : '-',
                            interval: contextValue.selectedGiftPrice?.interval,
                            tier_name: contextValue?.selectedGiftTier?.name,
                          }),
                          'upgrade.tabs.gift.form.purchaser_email': t('upgrade.tabs.gift.form.purchaser_email'),
                          'upgrade.tabs.gift.form.purchaser_name': t('upgrade.tabs.gift.form.purchaser_name'),
                          'upgrade.tabs.gift.form.recipient_email': t('upgrade.tabs.gift.form.recipient_email'),
                          'upgrade.tabs.gift.form.message': t('upgrade.tabs.gift.form.message'),
                          'upgrade.tabs.gift.form.message_placeholder': t('upgrade.tabs.gift.form.message_placeholder'),
                          'upgrade.tabs.gift.form.start_at': t('upgrade.tabs.gift.form.start_at'),
                        }}
                        currentFormMode={contextValue.currentFormMode}
                        onCurrentFormModeChange={contextValue.setCurrentFormMode}
                        StripeCardElement={
                          renderableNode.attrs.internalCheckout
                            ? (CardElement as StripeCardElementComponent)
                            : undefined
                        }
                        PaymentRequestButtonElement={
                          canShowPaymentRequestButton && paymentRequest
                            ? ((
                                <PaymentRequestButtonElement
                                  options={{
                                    paymentRequest,
                                  }}
                                />
                              ) as ReactNode)
                            : undefined
                        }
                        isLive
                      >
                        {children}
                      </PricingElement>
                    ) : null
                  }
                </UpgradeAction>
              </Elements>
            );
          }}
        </UpgradeContext.Consumer>
      </UpgradeProvider>
    </ErrorBoundary>
  );
};

export default Pricing;
